import { Sizes, Variants } from '@common/types';
import { DropdownItem, Input } from '@base-app';
import { yupResolver } from '@hookform/resolvers/yup';
import { NON_ADMIN_USER_ROLES, TEAMS_TRANSLATION } from '@modules/teams/constants';
import { getNonAdminGroup } from '@modules/teams/utils';
import { useState, useEffect, FC } from 'react';
import { Controller, SubmitHandler, useForm } from 'react-hook-form';
import { useTranslation } from 'react-i18next';
import { object, string } from 'yup';
import {
  ButtonStyled,
  FormButtonsStyled,
  FormFieldRoleStyled,
  FormFieldStyled,
  FormStyled,
  SelectStyled,
} from './AddTeamMemberForm.styles';
import { AddNewUserFormValues, AddTeamMemberFormProps } from './AddTeamMemberForm.types';
import { useQueryGroups } from '@modules/shared/hooks';
import { COMMON_TRANSLATION } from '@constants/index';
import { useUserFieldsValidation } from '@modules/shared/hooks/useUserFieldsValidation';

const INITIAL_FORM = {
  firstName: '',
  lastName: '',
  email: '',
  role: undefined,
};

const AddTeamMemberForm: FC<AddTeamMemberFormProps> = ({ onCancelForm, onSaveForm, user }) => {
  const { t } = useTranslation([COMMON_TRANSLATION, TEAMS_TRANSLATION]);
  const { data: groups } = useQueryGroups();
  const [isEditMode, setIsEditMode] = useState<boolean>(user !== undefined);
  const { firstName, lastName, email } = useUserFieldsValidation();

  const roles: DropdownItem[] = NON_ADMIN_USER_ROLES;

  const schema = object({
    firstName,
    lastName,
    email,
    role: string()
      .required(t('error_role_required', { ns: TEAMS_TRANSLATION }))
      .oneOf(NON_ADMIN_USER_ROLES.map((role) => role.id)),
  }).required();

  let defaultValues: Partial<AddNewUserFormValues> = INITIAL_FORM;

  if (isEditMode) {
    defaultValues = {
      firstName: user?.firstName,
      lastName: user?.lastName,
      email: user?.email,
      role: user?.role?.id,
    };
  }

  const {
    handleSubmit,
    control,
    formState: { errors, isDirty },
    reset,
  } = useForm<AddNewUserFormValues>({
    resolver: yupResolver(schema),
    defaultValues,
    mode: 'onBlur',
    reValidateMode: 'onBlur',
  });

  const resetForm = (): void => {
    reset(INITIAL_FORM);
  };

  const handleFormSubmit: SubmitHandler<AddNewUserFormValues> = (values) => {
    const selectedRole = roles.find((role) => role.id === values.role);

    if (selectedRole === undefined || groups === undefined) {
      return;
    }

    const group = getNonAdminGroup(groups);

    onSaveForm({
      ...values,
      role: {
        id: selectedRole.id,
        name: selectedRole.label,
        group,
      },
    });
    resetForm();
  };

  const handleResetForm = (): void => {
    resetForm();
  };

  const handleCancelForm = (): void => {
    onCancelForm?.();
  };

  useEffect(() => {
    setIsEditMode(user !== undefined);
  }, [user]);

  return (
    <FormStyled onSubmit={handleSubmit(handleFormSubmit)}>
      <FormFieldStyled css={{ '@sm': { paddingRight: '$8' } }}>
        <Controller
          name="firstName"
          control={control}
          render={({ field }) => (
            <Input
              {...field}
              id={field.name}
              name={field.name}
              label={t('first_name', { ns: 'teams' })}
              fullWidth
              error={!(errors.firstName == null)}
              errorDescription={errors.firstName?.message as string}
              placeholder={t('first_name_example', { ns: 'teams' })}
              testId="add-team-member-form-input-first-name"
            />
          )}
        />
      </FormFieldStyled>
      <FormFieldStyled>
        <Controller
          name="lastName"
          control={control}
          render={({ field }) => (
            <Input
              {...field}
              id={field.name}
              name={field.name}
              label={t('last_name', { ns: 'teams' })}
              fullWidth
              error={!(errors.lastName == null)}
              errorDescription={errors.lastName?.message as string}
              placeholder={t('last_name_example', { ns: 'teams' })}
              testId="add-team-member-form-input-last-name"
            />
          )}
        />
      </FormFieldStyled>
      <FormFieldStyled css={{ '@sm': { paddingRight: '$8' } }}>
        <Controller
          name="email"
          control={control}
          render={({ field }) => (
            <Input
              {...field}
              id={field.name}
              name={field.name}
              label={t('email', { ns: 'teams' })}
              type="email"
              fullWidth
              error={!(errors.email == null)}
              errorDescription={errors.email?.message as string}
              placeholder={t('email_example', { ns: 'teams' })}
              testId="add-team-member-form-input-email"
            />
          )}
        />
      </FormFieldStyled>
      <FormFieldRoleStyled>
        <Controller
          name="role"
          control={control}
          render={({ field }) => {
            const item = field.value as unknown as string;
            return (
              <SelectStyled
                {...field}
                testId="add-team-member-form-dropdown-role"
                defaultValue={item || ''}
                value={item || ''}
                error={!(errors.role == null)}
                errorDescription={errors.role?.message as string}
                id={field.name}
                items={roles}
                fullWidth
                label={t('role', { ns: 'teams' })}
                name={field.name}
                placeholder={t('select_role_example', { ns: 'teams' })}
                variant={Variants.PRIMARY}
              />
            );
          }}
        />
      </FormFieldRoleStyled>
      <FormButtonsStyled>
        <ButtonStyled
          data-testid={isEditMode ? 'add-team-member-form-button-save' : 'add-team-member-form-button-add'}
          size={Sizes.SMALL}
          type="submit"
          variant={Variants.SECONDARY}
        >
          {isEditMode ? t('save') : t('add')}
        </ButtonStyled>
        {!isEditMode && isDirty && (
          <ButtonStyled
            data-testid="add-team-member-form-button-clear"
            type="reset"
            variant={Variants.TERTIARY}
            onClick={handleResetForm}
          >
            {t('clear')}
          </ButtonStyled>
        )}
        {isEditMode && (
          <ButtonStyled type="button" variant={Variants.TERTIARY} onClick={handleCancelForm}>
            {t('cancel')}
          </ButtonStyled>
        )}
      </FormButtonsStyled>
    </FormStyled>
  );
};

export default AddTeamMemberForm;
